import AppCommandBar from 'app/AppCommandBar'
import { AutoStack } from 'components'
import { RichTextViewer } from 'components/RichTextViewer/RichTextViewer'
import { ResourceCategory } from 'context'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  ProvingDetailsOrderIdForm,
  ProvingDetailsResourceRequirementStatusForm,
  ProvingDetailsTimespanForm,
  ProvingDetailsTimespanFormSchema,
  ProvingDetailsUsageTimeForm,
  ResourceBookingDetailsForm,
} from 'forms/ProvingDetailsForm'
import {
  ProvingDetailsResourceBookingForm,
  ProvingDetailsResourceBookingFormSchema,
  ProvingDetailsResourceBookingFormValues,
} from 'forms/ProvingDetailsForm/resourceBookings/ProvingDetailsResourceBookingForm'
import { ProvingDetailsResourceParticipantForm } from 'forms/ProvingDetailsForm/resourceBookings/ProvingDetailsResourceParticipantForm'
import { ProvingDetailsAnnotationForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsAnnotationForm'
import {
  ProvingUserRole,
  Resource,
  ResourceBooking,
  ResourceBookingAddInput,
  ResourceBookingBatchInput,
  ResourceBookingUpdateInput,
  ResourceSelectionType,
  UserRole,
} from 'gql.generated'
import {
  canUpdateProving,
  isInProvingRole,
  omitDeep,
  removeNullProperties,
} from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCommand, useDeselectCommand } from 'hooks/useCommand'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import { sortBy } from 'lodash'
import { IconButton, PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAddResourceBookingMutation,
  useDeleteResourceBookingMutation,
  useResourcesQuery,
  useUpdateProvingMutation,
  useUpdateResourceBookingMutation,
} from './hooks/index'
import { Page, Section } from './Layout'
import { ResourceBookingList, ResourceRequirementList } from './lists/index'

enum FormType {
  addResourceBookings,
  timespan,
  status,
  orderId,
  participant,
  annotation,
  usageTime,
}

export const PageResourceBookings: React.FC = () => {
  const { t } = useTranslation()
  const [formType, setFormType] = React.useState<FormType>()
  const [editPanelState, showEditPanel, hideEditPanel] = useFormikPanelState()
  const [selectedItemId, setSelectedItemId] = React.useState()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [timespanPanelState, showTimespanPanel, hideTimespanPanel] =
    useFormikPanelState()
  const [annotationPanelState, showAnnotationPanel, hideAnnotationPanel] =
    useFormikPanelState()
  const [multiEditPanelState, showMultiEditPanel, hideMultiEditPanel] =
    useFormikPanelState()
  const [usageTimePanelState, showUsageTimePanel, hideUsageTimePanel] =
    useFormikPanelState()
  const [dialogState, showDialog, hideDialog] = useDialogState()

  const [proving, refetch] = useCurrentProving()

  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)

  // const isResourceManager = auth.hasUserRole(UserRole.ResourceManager)
  const isProvingResourceManager = isInProvingRole(
    proving,
    ProvingUserRole.ResourceManager
  )
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const canUpdateCurrentProving = canUpdateProving(proving)
  const canUseCommand = canUpdateCurrentProving && !isMaintenanceHead
  const [selection, selectedItems] = useSelection<ResourceBooking>({
    getKey: (item) => item._id,
  })

  const resourceRequirements = proving.resourceRequirements || []
  const resourceBookings = proving.resourceBookings || []
  const { data } = useResourcesQuery({
    variables: {
      provingGroundId: proving!.provingGround!._id,
    },
  })

  const participants = proving.participants || []
  const resources = data && data!.resources ? data.resources : []
  const resourceCategories: ResourceCategory[] = React.useMemo(
    () =>
      data && data.resourceCategories
        ? sortBy(
            data.resourceCategories
              .filter((resCategory) => resCategory._id !== 'provingGround')
              .map((resCategory) => ({
                id: resCategory._id,
                title: t(`field.category.${resCategory._id}`),
                types: sortBy(
                  resCategory.types.map((type) => ({
                    id: type,
                    title: t(`field.type.${type}`, {
                      category: t(`field.category.${resCategory._id}`),
                    }),
                  })),
                  'title'
                ),
              })),
            'title'
          )
        : [],
    [data, t]
  )

  const [addResourceBooking] = useAddResourceBookingMutation()
  const [updateResourceBooking] = useUpdateResourceBookingMutation()
  const [deleteResourceBooking] = useDeleteResourceBookingMutation()
  const [updateProving] = useUpdateProvingMutation()

  const handleSubmit = React.useCallback(
    async (
      values: any,
      actions: FormikActions<ProvingDetailsResourceBookingFormValues>
    ) => {
      const itemsToAdd: ResourceBookingAddInput[] =
        values.resources !== undefined
          ? values.resources.map(
              (res: Resource) =>
                ({
                  resource: res._id,
                  dateFrom:
                    values.dateFrom === undefined
                      ? proving.dateFrom
                      : values.dateFrom,
                  dateTo:
                    values.dateTo === undefined
                      ? proving.dateTo
                      : values.dateTo,
                } as ResourceBookingAddInput)
            )
          : ([] as ResourceBookingAddInput[])

      // const item = {}
      actions.setSubmitting(true)
      const { errors } = await addResourceBooking({
        variables: {
          provingId: proving._id,
          input: itemsToAdd,
        },
      })
      if (errors) {
        throw errors
      }

      actions.setSubmitting(false)
      refetch()
      hidePanel()
    },
    [proving, addResourceBooking, hidePanel, refetch]
  )

  const handleSubmitMulti = React.useCallback(
    async (
      values: ResourceBookingUpdateInput,
      actions: FormikActions<ResourceBookingUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const batches: ResourceBookingBatchInput[] = []
      selectedItems.forEach(async (rr) => {
        batches.push({ _id: rr._id, update: values })
      })
      const { errors } = await updateResourceBooking({
        variables: { provingId: proving._id, batch: batches },
      })
      if (errors) {
        throw errors
      }
      selection.setAllSelected(false)
      refetch()
      actions.setSubmitting(false)
      hideMultiEditPanel()
      hideTimespanPanel()
      hideUsageTimePanel()
    },
    [
      proving,
      hideUsageTimePanel,
      updateResourceBooking,
      hideMultiEditPanel,
      selectedItems,
      refetch,
      selection,
      hideTimespanPanel,
    ]
  )

  const handleConfirmDelete = async () => {
    const ids: any[] = []
    selectedItems.forEach(async (rr) => {
      ids.push(rr._id)
    })
    const { errors } = await deleteResourceBooking({
      variables: {
        provingId: proving._id,
        idsToDelete: ids,
      },
    })
    if (errors) {
      throw errors
    }
    selection.setAllSelected(false)
    refetch()
    hideDialog()
  }

  useCommand({
    key: 'addResourceBooking',
    priority: 10,
    text: t('model.proving.resourceBookings.addResourceBooking'),
    iconProps: { iconName: 'Manufacturing' },
    headerText: t('model.proving.resourceBookings.addResourceBooking'),
    disabled:
      proving.status === 8 ||
      proving.status === 9 ||
      (!isAdmin && !isProvingResourceManager && !isServiceTeam),
    onClick: () => {
      showPanel({ dateFrom: proving.dateFrom, dateTo: proving.dateTo })
      setFormType(FormType.addResourceBookings)
    },
  })

  //#region TIMESPAN
  const setResourceBookingDateToProvingDate = React.useCallback(async () => {
    const batch: ResourceBookingBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        dateFrom: proving.dateFrom,
        dateTo: proving.dateTo,
      },
    }))

    await updateResourceBooking({
      variables: {
        provingId: proving._id,
        batch,
      },
    })

    refetch()
  }, [proving, selectedItems, updateResourceBooking, refetch])

  useCommand(
    {
      key: 'timespan',
      priority: 20,
      text: t('model.proving.resourceRequirements.timespan'),
      iconProps: { iconName: 'CalendarWeek' },
      disabled:
        proving.status === 8 ||
        proving.status === 9 ||
        selectedItems.length === 0 ||
        (!isAdmin && !isProvingResourceManager && !isServiceTeam) ||
        selectedItems.some((sel) => !!sel.resource.external),
      subMenuProps: {
        items: [
          {
            key: 'setResourceBookingTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              showTimespanPanel(
                selectedItems.length === 1
                  ? {
                      dateFrom: selectedItems[0].dateFrom,
                      dateTo: selectedItems[0].dateTo,
                    }
                  : {}
              )
              setFormType(FormType.timespan)
            },
          },
          {
            key: 'setResourceBookingDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setResourceBookingDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  //#region ORDERID
  const setNoOrderId = React.useCallback(async () => {
    const batch: ResourceBookingBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        orderId: '',
      },
    }))

    await updateResourceBooking({
      variables: {
        provingId: proving._id,
        batch,
      },
    })

    refetch()
  }, [proving, selectedItems, updateResourceBooking, refetch])

  useCommand(
    {
      key: 'orderId',
      disabled:
        proving.status === 8 ||
        proving.status === 9 ||
        selectedItems.length === 0 ||
        (!isAdmin && !isProvingResourceManager && !isServiceTeam),
      priority: 30,
      text: t('model.proving.vehicle.orderId'),
      iconProps: { iconName: 'DonutChart' },
      headerText: t('model.proving.vehicle.orderId'),
      subMenuProps: {
        items: [
          {
            key: 'assignOrderId',
            text: t('field.orderId.assignOrderId'),
            onClick: () => {
              selectedItems.length > 1
                ? showMultiEditPanel({})
                : showMultiEditPanel({})
              setFormType(FormType.orderId)
            },
          },
          {
            key: 'noOrderId',
            text: t('field.orderId.noOrderId'),
            onClick: () => {
              setNoOrderId()
            },
          },
        ],
      },
    },
    [selectedItems]
  )
  //#endregion ORDERID

  useCommand(
    {
      key: 'participant',
      priority: 50,
      disabled:
        proving.status === 8 ||
        proving.status === 9 ||
        selectedItems.length === 0 ||
        (!isAdmin && !isProvingResourceManager && !isServiceTeam),
      text: t('model.proving.resourceBookings.participant'),
      iconProps: { iconName: 'Contact' },
      onClick: () => {
        showMultiEditPanel({})
        setFormType(FormType.participant)
      },
    },
    [selectedItems]
  )

  // #region USAGETIME

  useCommand(
    {
      key: 'usageTime',
      priority: 60,
      disabled:
        proving.status === 8 ||
        proving.status === 9 ||
        selectedItems.length > 1 ||
        (!isAdmin && !isProvingResourceManager && !isServiceTeam),
      hidden:
        !(
          selectedItems &&
          selectedItems[0] &&
          selectedItems[0].resource &&
          selectedItems[0].resource.type === ResourceSelectionType.TestTrack
        ) &&
        !(
          selectedItems &&
          selectedItems[0] &&
          selectedItems[0].resource &&
          selectedItems[0].resource.type ===
            ResourceSelectionType.TestTrackUsage
        ),
      text: t('model.proving.resourceBookings.usageTime'),
      iconProps: { iconName: 'Edit' },
      onClick: () => {
        showUsageTimePanel({})
        setFormType(FormType.usageTime)
      },
    },
    [selectedItems]
  )
  // #endregion USAGETIME

  useDeselectCommand(
    selectedItems.length,
    {
      priority: 0,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )
  useCommand(
    {
      key: 'delete',
      priority: 10,
      farCommand: true,
      text: t('model.proving.resourceBookings.deleteResourceBooking'),
      hidden: selectedItems.length === 0,
      disabled:
        proving.status === 8 ||
        proving.status === 9 ||
        (!isAdmin && !isProvingResourceManager && !isServiceTeam),
      iconProps: { iconName: 'Delete' },
      onClick: () => showDialog(),
    },
    [selectedItems]
  )

  const handleDetailsViewSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const updatedValues = removeNullProperties({
        orderId: values.orderId,
        dateFrom: values.dateFrom,
        dateTo: values.dateTo,
        status: values.status,
        usageTime: values.usageTime,
        participantId: values.participantId,
        _id: values._id,
      })
      const { errors } = await updateResourceBooking({
        variables: {
          provingId: proving._id,
          batch: [
            {
              _id: selectedItemId,
              update: omitDeep(updatedValues, [
                '__typename',
                '_id',
                '_changes',
                'resource',
                'category',
                'title',
                'type',
                'usageTime',
                'parent',
                'erpnr',
                'provingId',
              ]),
            },
          ],
        },
      })
      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideEditPanel()
    },
    [
      hideEditPanel,
      proving,
      refetch,
      selection,
      updateResourceBooking,
      selectedItemId,
    ]
  )

  const handleAnnotationSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const { errors } = await updateProving({
        variables: {
          id: proving.id,
          update: {
            annotationResourceBookings: values.annotation,
          },
        },
      })
      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      refetch()
      hideAnnotationPanel()
    },
    [proving, updateProving, hideAnnotationPanel, refetch]
  )

  const onItemInvoked = (item: ResourceBooking): void => {
    setSelectedItemId(item._id)
    showEditPanel({
      ...item,
      title: item.resource.title,
      resource: item.resource._id,
      category: item.resource.category,
      type: item.resource.type,
      parent: item.resource.parent,
    })
  }

  const resourceBooking = localStorage.getItem('resourceBooking')
  let currentResourceBooking: ResourceBooking

  if (resourceBooking && resourceCategories.length > 0) {
    currentResourceBooking = JSON.parse(resourceBooking)
    localStorage.removeItem('resourceBooking')
    setSelectedItemId(currentResourceBooking._id)
    showEditPanel({
      ...currentResourceBooking,
      resource: currentResourceBooking.resource._id,
      type: currentResourceBooking.resource.type,
      category: currentResourceBooking.resource.category,
      parent: currentResourceBooking.resource.parent,
      dateFrom: currentResourceBooking.dateFrom,
      dateTo: currentResourceBooking.dateTo,
    })
  }

  return (
    <Page>
      <Section label={t('section.resourceBookings.annotation')}>
        <AutoStack horizontal={true}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            disabled={!canUseCommand}
            onClick={() => {
              setFormType(FormType.annotation)
              showAnnotationPanel({
                annotation: proving.annotationResourceBookings || '',
              })
            }}
          />

          <RichTextViewer
            richText={
              proving.annotationResourceBookings
                ? proving.annotationResourceBookings
                : ''
            }
          />
        </AutoStack>
      </Section>
      <Section label={t('section.resourceBookings.header')}>
        <AppCommandBar />
        <ResourceBookingList
          items={resourceBookings}
          selection={selection}
          onItemInvoked={onItemInvoked}
        />
      </Section>
      <Section label={t('section.resourceRequirements.header')}>
        <ResourceRequirementList items={resourceRequirements} readonly={true} />
      </Section>

      {formType === FormType.addResourceBookings ? (
        <FormikPanel
          {...panelState}
          // tslint:disable-next-line: no-console
          onSubmit={handleSubmit}
          validationSchema={new ProvingDetailsResourceBookingFormSchema()}
        >
          <ProvingDetailsResourceBookingForm
            resources={resources}
            resourceCategories={resourceCategories}
          />
        </FormikPanel>
      ) : (
        <FormikPanel {...multiEditPanelState} onSubmit={handleSubmitMulti}>
          {formType !== undefined && formType === FormType.orderId && (
            <ProvingDetailsOrderIdForm
              orderIds={proving.orderIdDistribution!.map((x: any) => x)}
            />
          )}
          {formType === FormType.status && (
            <ProvingDetailsResourceRequirementStatusForm />
          )}

          {formType === FormType.participant && (
            <ProvingDetailsResourceParticipantForm
              participants={participants}
            />
          )}
        </FormikPanel>
      )}
      <FormikPanel {...annotationPanelState} onSubmit={handleAnnotationSubmit}>
        {formType !== undefined && formType === FormType.annotation && (
          <ProvingDetailsAnnotationForm />
        )}
      </FormikPanel>
      <FormikPanel
        {...timespanPanelState}
        onSubmit={handleSubmitMulti}
        validationSchema={
          new ProvingDetailsTimespanFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {formType === FormType.timespan && (
          <ProvingDetailsTimespanForm proving={proving} />
        )}
      </FormikPanel>
      <FormikPanel {...usageTimePanelState} onSubmit={handleSubmitMulti}>
        {formType === FormType.usageTime && <ProvingDetailsUsageTimeForm />}
      </FormikPanel>
      <FormikPanel
        {...editPanelState}
        onSubmit={handleDetailsViewSubmit}
        type={PanelType.large}
      >
        <ResourceBookingDetailsForm
          participants={participants}
          orderIds={proving.orderIdDistribution!.map((x: any) => x)}
          resourceCategories={resourceCategories}
          disableDatePickers={
            selectedItems[0] && !!selectedItems[0].resource.external
          }
        />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
      {/* <Section label={t('section.debug.header')}>
        <pre>{JSON.stringify(proving, null, 2)}</pre>
      </Section> */}
    </Page>
  )
}
